import { useRef, useState } from "react";
import { Eye, Download } from "lucide-react";
export interface DocumentRequest {
  visaCaseId?: string | null;
  applicationId?: number;
  id: string;
  originId: string;
  label: string;
  category: string;
  documentId?: number | null;
  suppliedAt?: string | null;
  createdAt?: string;
  requester?: { firstName?: string; lastName?: string };
  requestedBy?: number;
  document?: {
    id: number;
    fileName?: string;
    originalName?: string;
    path?: string;
    mimeType?: string;
  };
}
export function DocumentRequestList({
  requests,
  notes,
  onUpload,
  onDownload,
  onPreview,
}: {
  requests: DocumentRequest[];
  notes?: unknown;
  onUpload?: (
    request: DocumentRequest,
    file: File,
    operationId: string,
  ) => Promise<void>;
  onDownload: (request: DocumentRequest) => void;
  onPreview?: (request: DocumentRequest) => void;
}) {
  const [files, setFiles] = useState<Record<string, File>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const operations = useRef<Record<string, string>>({});
  const locks = useRef(new Set<string>());
  if (!requests.length) return null;
  let originNotes: any[] = [];
  try {
    const parsed = typeof notes === "string" ? JSON.parse(notes) : notes;
    if (Array.isArray(parsed))
      originNotes = parsed.filter((n) => n && typeof n === "object");
  } catch {
    /* Legacy plain text cannot be assigned to an origin. */
  }
  const outstanding = requests.filter((r) => !r.documentId).length;
  const groups = requests.reduce<Record<string, DocumentRequest[]>>((a, r) => {
    (a[r.originId || "legacy"] ||= []).push(r);
    return a;
  }, {});
  return (
    <section className="card mb-4 shadow-sm" aria-label="Document requests">
      <div className="card-header">
        <h5 className="mb-0">Document requests</h5>
      </div>
      <div className="card-body">
        <p aria-live="polite">
          {outstanding} outstanding · {requests.length - outstanding} supplied
        </p>
        {Object.entries(groups).map(([origin, rows], index) => (
          <div key={origin} className="border rounded p-3 mb-3">
            <h6>
              Request group {index + 1}
              {origin.startsWith("legacy")
                ? " (legacy application request)"
                : ""}
            </h6>
            {(() => {
              const note = originNotes.find(
                (n) => n.noteId === origin || n.updateId === origin,
              );
              return rows[0]?.visaCaseId ? (<a href={`/applications/${rows[0]?.applicationId}/visa-support`}>View Visa Support guidance</a>) : note ? (
                <div className="mb-3" style={{ overflowWrap: "anywhere" }}>
                  <p className="mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    {note.note}
                  </p>
                  <small className="text-muted">
                    {note.addedByName || "Administrator"}
                    {note.timestamp
                      ? ` · ${new Date(note.timestamp).toLocaleString()}`
                      : ""}
                  </small>
                </div>
              ) : (
                <p className="small text-muted">
                  {origin.startsWith("legacy")
                    ? "Original note unavailable for this legacy application request."
                    : "Original note unavailable."}
                </p>
              );
            })()}
            {rows.map((r) => (
              <div
                key={r.id}
                className="border-top pt-3 mt-3"
                style={{ minWidth: 0, overflowWrap: "anywhere" }}
              >
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <strong>{r.label}</strong>
                  <span
                    className={`badge ${r.documentId ? "bg-success" : "bg-warning text-dark"}`}
                  >
                    {r.documentId ? "Supplied" : "Outstanding"}
                  </span>
                </div>
                <div className="small text-muted mb-2">
                  Category: {r.category}
                  {r.createdAt
                    ? ` · Requested ${new Date(r.createdAt).toLocaleString()}`
                    : ""}
                  {r.requester
                    ? ` by ${[r.requester.firstName, r.requester.lastName].filter(Boolean).join(" ")}`
                    : ""}
                </div>
                {r.documentId ? (
                  <>
                    <p className="small">
                      File supplied
                      {r.suppliedAt
                        ? ` on ${new Date(r.suppliedAt).toLocaleString()}`
                        : ""}
                      . Supply does not indicate approval.
                    </p>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <strong className="small" style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {r.document?.originalName || r.document?.fileName || r.label}
                      </strong>
                      <div className="d-flex gap-1 flex-shrink-0 ms-auto">
                        {onPreview && (r.document?.mimeType?.startsWith("image/") || r.document?.mimeType === "application/pdf") && (
                          <button type="button" className="btn btn-sm btn-outline-primary"
                            title="Preview" aria-label={`Preview ${r.document?.originalName || r.document?.fileName || r.label}`}
                            onClick={() => onPreview(r)}>
                            <Eye size={14} />
                          </button>
                        )}
                        <button type="button" className="btn btn-sm btn-outline-secondary"
                          title="Download" aria-label={`Download ${r.document?.originalName || r.document?.fileName || r.label}`}
                          onClick={() => onDownload(r)}>
                          <Download size={14} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  onUpload && (
                    <>
                      <label
                        className="form-label"
                        htmlFor={`request-file-${r.id}`}
                      >
                        File for {r.label}
                      </label>
                      <input
                        id={`request-file-${r.id}`}
                        type="file"
                        className="form-control mb-2"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        disabled={busy[r.id]}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          setErrors((p) => ({ ...p, [r.id]: "" }));
                          if (file) {
                            setFiles((p) => ({ ...p, [r.id]: file }));
                            operations.current[r.id] = crypto.randomUUID();
                          } else {
                            setFiles((p) => {
                              const next = { ...p };
                              delete next[r.id];
                              return next;
                            });
                            delete operations.current[r.id];
                          }
                        }}
                      />
                      <p className="small text-muted">
                        One file per request. PDF, JPG, PNG, DOC or DOCX;
                        maximum 10 MB.
                      </p>
                      {errors[r.id] && (
                        <p className="text-danger" role="alert">
                          {errors[r.id]}
                        </p>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!files[r.id] || busy[r.id]}
                        onClick={async () => {
                          if (locks.current.has(r.id) || !files[r.id]) return;
                          if (files[r.id].size > 10 * 1024 * 1024) {
                            setErrors((p) => ({
                              ...p,
                              [r.id]: "File must be 10 MB or smaller",
                            }));
                            return;
                          }
                          const selected = files[r.id];
                          const allowedTypes = [
                            "application/pdf",
                            "image/jpeg",
                            "image/png",
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                          ];
                          if (
                            !/\.(pdf|jpe?g|png|docx?)$/i.test(selected.name) ||
                            (selected.type &&
                              !allowedTypes.includes(selected.type))
                          ) {
                            setErrors((p) => ({
                              ...p,
                              [r.id]:
                                "Choose a PDF, JPG, PNG, DOC or DOCX file. The selected file was not uploaded.",
                            }));
                            return;
                          }
                          locks.current.add(r.id);
                          setBusy((p) => ({ ...p, [r.id]: true }));
                          setErrors((p) => ({ ...p, [r.id]: "" }));
                          try {
                            await onUpload(
                              r,
                              files[r.id],
                              (operations.current[r.id] ||=
                                crypto.randomUUID()),
                            );
                          } catch (e: any) {
                            setErrors((p) => ({
                              ...p,
                              [r.id]:
                                e?.response?.data?.error ||
                                e.message ||
                                "Upload failed. Your selection is retained; retry this request.",
                            }));
                          } finally {
                            locks.current.delete(r.id);
                            setBusy((p) => ({ ...p, [r.id]: false }));
                          }
                        }}
                      >
                        {busy[r.id] ? "Uploading…" : "Supply document"}
                      </button>
                    </>
                  )
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
